import * as React from 'react'
// TODO(icons): approx — was Lucide ChevronsUpDownIcon
import { ArrowDownIcon, ArrowUpIcon, SortIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'

// DS Table: header row bg = --fill-neutral-lightest, height ~48 px.
// Body rows: hover = --fill-neutral-lightest, selected = --fill-action-lightest.
// Cell padding: 12 px vertical (py-3), 16 px horizontal (px-4).
// Border: --border between rows; no outer border on container.

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table data-slot="table" className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  // DS: header row has a muted background to visually distinguish it from body rows
  return <thead data-slot="table-header" className={cn('bg-muted/50 [&_tr]:border-b', className)} {...props} />
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  )
}

type TableRowProps = React.ComponentProps<'tr'> & {
  selectable?: boolean
  selected?: boolean
}

function TableRow({ className, selectable = false, selected = false, ...props }: TableRowProps) {
  return (
    <tr
      data-slot="table-row"
      data-selectable={selectable || undefined}
      data-selected={selected || undefined}
      aria-selected={selectable ? selected : undefined}
      className={cn(
        'border-b transition-colors',
        // DS hover: neutral-lightest tint
        'hover:bg-muted/50',
        selectable && 'cursor-pointer',
        // DS selected: action-lightest (#ECF3FF) background
        selected ? 'bg-accent' : 'data-[state=selected]:bg-muted',
        className,
      )}
      {...props}
    />
  )
}

type SortDirection = 'asc' | 'desc'
type CellAlign = 'left' | 'center' | 'right'

type TableHeadProps = React.ComponentProps<'th'> & {
  align?: CellAlign
  sortable?: boolean
  sorted?: boolean
  sortDirection?: SortDirection
  onSort?: () => void
}

function TableHead({
  className,
  align = 'left',
  sortable = false,
  sorted = false,
  sortDirection,
  onSort,
  children,
  ...props
}: TableHeadProps) {
  return (
    <th
      data-slot="table-head"
      data-align={align}
      data-sortable={sortable || undefined}
      data-sorted={sorted || undefined}
      className={cn(
        // DS header: h-12 (48 px), px-4 horizontal padding, text-muted-foreground label colour
        'h-12 px-4 align-middle text-xs font-medium whitespace-nowrap uppercase tracking-wide text-muted-foreground',
        '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        sortable && 'cursor-pointer select-none hover:bg-muted/50',
        // Sorted column: foreground text to indicate active sort
        sorted && 'text-foreground',
        className,
      )}
      onClick={sortable ? onSort : undefined}
      aria-sort={sorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : sortable ? 'none' : undefined}
      {...props}
    >
      {sortable ? (
        <span className="inline-flex items-center gap-1">
          {children}
          {sorted ? (
            sortDirection === 'asc' ? (
              <ArrowUpIcon className="size-3.5 shrink-0" />
            ) : (
              <ArrowDownIcon className="size-3.5 shrink-0" />
            )
          ) : (
            <SortIcon size="xs" className="shrink-0 opacity-50" />
          )}
        </span>
      ) : (
        children
      )}
    </th>
  )
}

type TableCellProps = React.ComponentProps<'td'> & {
  align?: CellAlign
}

function TableCell({ className, align = 'left', ...props }: TableCellProps) {
  return (
    <td
      data-slot="table-cell"
      data-align={align}
      className={cn(
        // DS body cell: px-4 py-3 = 16 px horizontal, 12 px vertical → row height ~48 px with 2 × py-3
        'px-4 py-3 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption data-slot="table-caption" className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  )
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
